import { DisablableOption } from "@models";
import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

export type CalendarSystemType = "Standard" | "Galifar";

export interface CalendarSystem {
  true_id: string;
  name: string;
  abbrev: string;
  very_first_wkday: number; // What day of the week was it on the first day of the first year
  type: string;
  months: CalendarMonth[];
  weekdays: CalendarWeekday[];
  holidays: CalendarHoliday[];
  seasons: CalendarSeason[];
  years_per_leap_year: number;
  restart_weekdays_on_new_year?: boolean; // If this is true, then the first day of the year is always the first day of the week (Sunday equivalent).
}

export interface CalendarMonth {
  name: string;
  days: number;
  position: number;
  leap_day?: boolean;
  associated_symbol?: string;
  no_weekdays?: boolean; // If this is true, then this month ignores weekdays. It's generally as a sort of special holiday, and helps keep the year on schedule.
}

export interface CalendarWeekday {
  name: string;
  position: number;
}

export interface CalendarHoliday {
  name: string;
  type: "Holy Event" | "Anniversary" | "Sporting Event" | "Celebration";
  year_day?: number; // This is the day it starts as measured in days since the start of the year. New Year's Day is 1. Leap Days are ignored for the calculation of this, so Christmas is 359 and New Year's Eve is 365.
  mmdd?: number; // This is the day it happens in mmdd format. New Year's Day is 101. Christmas is 1225. New Year's Eve is 1231.
  length?: number; // If undefined then it's just 1 day. 
  nth_wkday?: { // This is for holidays that happen on the nth weekday of a particular month, like Thanksgiving is the 4th Thursday of November.
    mm?: number; // If this is defined, then it's the position of the month this applies to. If undefined then it's every month. For Thanksgiving it's 11.
    n: number; // How many of that weekday in the month to get to the holiday. For Thanksgiving it's 4.
    wkday: number; // The position of the weekday it's on. For Thanksgiving it's 5.
  };
  description?: string;
  observed_by?: string;
}

export interface CalendarSeason {
  name: string;
  mmdd_start: number; // This is the day it starts in mmdd format. New Year's Day is 101. Christmas is 1225. New Year's Eve is 1231.
  mmdd_end: number;
  year_day: number; // This is the day it starts as measured in days since the start of the year. New Year's Day is 1. Leap Days are ignored for the calculation of this, so Christmas is 359 and New Year's Eve is 365.
  length: number;
}

export const STANDARD_CALENDAR: CalendarSystem = {
  true_id: "STANDARD",
  type: "Standard",
  name: "Standard",
  abbrev: "AD",
  very_first_wkday: 7,
  months: [
    { name: "January", days: 31, position: 1 },
    { name: "February", days: 28, position: 2, leap_day: true },
    { name: "March", days: 31, position: 3 },
    { name: "April", days: 30, position: 4 },
    { name: "May", days: 31, position: 5 },
    { name: "June", days: 30, position: 6 },
    { name: "July", days: 31, position: 7 },
    { name: "August", days: 31, position: 8 },
    { name: "September", days: 30, position: 9 },
    { name: "October", days: 31, position: 10 },
    { name: "November", days: 30, position: 11 },
    { name: "December", days: 31, position: 12 },
  ],
  weekdays: [
    { name: "Sunday", position: 1 },
    { name: "Monday", position: 2 },
    { name: "Tuesday", position: 3 },
    { name: "Wednesday", position: 4 },
    { name: "Thursday", position: 5 },
    { name: "Friday", position: 6 },
    { name: "Saturday", position: 7 },
  ],
  holidays: [
    { name: "New Year's Day", type: "Anniversary", year_day: 1, mmdd: 101 },
    { name: "Halloween", type: "Holy Event", year_day: 303, mmdd: 1031 },
    { name: "Thanksgiving", type: "Anniversary", nth_wkday: { mm: 11, n: 4, wkday: 5 } },
    { name: "Christmas", type: "Holy Event", year_day: 359, mmdd: 1225 },
    { name: "New Year's Eve", type: "Anniversary", year_day: 365, mmdd: 1231 }
  ],
  seasons: [
    { name: "Spring", mmdd_start: 320, mmdd_end: 620, year_day: 80, length: 93 },
    { name: "Summer", mmdd_start: 621, mmdd_end: 921, year_day: 173, length: 93 },
    { name: "Autumn", mmdd_start: 922, mmdd_end: 1220, year_day: 266, length: 90 },
    { name: "Winter", mmdd_start: 1221, mmdd_end: 319, year_day: 356, length: 89 },
  ],
  years_per_leap_year: 4
}

export const GALIFAR_CALENDAR: CalendarSystem = {
  true_id: "GALIFAR",
  type: "Galifar",
  name: "Galifar",
  abbrev: "YK",
  very_first_wkday: 1,
  restart_weekdays_on_new_year: true,
  months: [
    { name: "Zarantyr (Jan)", days: 28, position: 1, associated_symbol: "Storm" },
    { name: "Olarune (Feb)", days: 28, position: 2, associated_symbol: "Sentinel" },
    { name: "Therendor (Mar)", days: 28, position: 3, associated_symbol: "Healing" },
    { name: "Eyre (Apr)", days: 28, position: 4, associated_symbol: "Making" },
    { name: "Dravago (May)", days: 28, position: 5, associated_symbol: "Handling" },
    { name: "Nymm (Jun)", days: 28, position: 6, associated_symbol: "Hospitality" },
    { name: "Lharvion (Jul)", days: 28, position: 7, associated_symbol: "Detection" },
    { name: "Barrakas (Aug)", days: 28, position: 8, associated_symbol: "Finding" },
    { name: "Rhaan (Sep)", days: 28, position: 9, associated_symbol: "Scribing" },
    { name: "Sypheros (Oct)", days: 28, position: 10, associated_symbol: "Shadow" },
    { name: "Aryth (Nov)", days: 28, position: 11, associated_symbol: "Passage" },
    { name: "Vult (Dec)", days: 28, position: 12, associated_symbol: "Warding" },
  ],
  weekdays: [
    { name: "Sul (Sunday)", position: 1 },
    { name: "Mol (Monday)", position: 2 },
    { name: "Zol (Tuesday)", position: 3 },
    { name: "Wir (Wednesday)", position: 4 },
    { name: "Zor (Thursday)", position: 5 },
    { name: "Far (Friday)", position: 6 },
    { name: "Sar (Saturday)", position: 7 },
  ],
  holidays: [
    { name: "Crystalfall", type: "Celebration", year_day: 37, mmdd: 209, description: "Celebration/Contest	Sharn	To celebrate the fallen Glass Tower sculptors carve ice towers and throw them into the Dagger River" },
    { name: "The Day of Mourning", type: "Anniversary", year_day: 48, mmdd: 220, description: "Anniversary	Khorvaire	This marks The Day of Mourning, when Cyre was destroyed. People observe in many ways." },
    { name: "Sun's Blessing", type: "Holy Event", year_day: 71, mmdd: 315 },
    { name: "Aureon's Crown", type: "Holy Event", year_day: 138, mmdd: 526 },
    { name: "Brightblade", type: "Holy Event", year_day: 152, mmdd: 612 },
    { name: "The Race of Eight Winds", type: "Sporting Event", year_day: 191, mmdd: 723, observed_by: "Sharn", description: "Contestants fly all around Sharn in attempt to win the race and prestige." },
    { name: "The Hunt", type: "Holy Event", year_day: 200, mmdd: 804 },
    { name: "Fathen's Fall", type: "Holy Event", year_day: 221, mmdd: 825 },
    { name: "Boldrei's Feast", type: "Holy Event", year_day: 233, mmdd: 909 },
    { name: "Wildnight", type: "Holy Event", year_day: 270, mmdd: 1018, length: 2, observed_by: "Khorvaire", description: "At sunset on Wildnight, worshipers and the nonfaithful alike give vent to their passions, in a raucous festival dedicated to the Fury." },
    { name: "Thronehold", type: "Holy Event", year_day: 291, mmdd: 1111, observed_by: "Khorvaire", description: "This marks the day of the signing of the Treaty of Thronehold and the end of The Last War." },
    { name: "Long Shadows", type: "Holy Event", year_day: 334, mmdd: 1226, length: 3, observed_by: "Khorvaire", description: "Dark magic dominates this three-day period that honors the Shadow." },
    // { name: "Revelation's Day", type: "Holy Event", mmdd: 1313, observed_by: "Blood of Vol", description: "A day venerated by the Blood of Vol, changes every year due to their 13 month Calendar. It's on the 13th day of the 13th month. From what I've read, the standard consensus is that before the destruction of the 13th moon, the year had 364 days, with a 13th 28 day month. Now the years are shorter, but the Blood of Vol still uses 13 months, causing their months to cycle throughout the year. Honestly I think that's weirder than my idea that there are actually 338 days in the year, with the last two days being celebrated as a kind of holiday that doesn't count as part of any month, and then BoV use the original 13 month calendar of 26 day months." },
  ],
  seasons: [
    { name: "Spring", mmdd_start: 320, mmdd_end: 620, year_day: 77, length: 85 },
    { name: "Summer", mmdd_start: 621, mmdd_end: 921, year_day: 162, length: 85 },
    { name: "Autumn", mmdd_start: 922, mmdd_end: 1220, year_day: 247, length: 83 },
    { name: "Winter", mmdd_start: 1221, mmdd_end: 319, year_day: 330, length: 83 },
  ],
  years_per_leap_year: 0 // No Leap Year
}

/** 
 * This is my revised Galifar calendar that has a 338 day year, with the last 2 days not being considered part of any month. 
 * I'm also moving the holiday Long Shadows to be during those 2 days.
 * 
 * My BLOOD_OF_VOL_CALENDAR_338 is the one the Blood of Vol use for this system.
 */
export const GALIFAR_CALENDAR_338: CalendarSystem = {
  true_id: "GALIFAR_REVISED",
  type: "Galifar",
  name: "Galifar",
  abbrev: "YK",
  very_first_wkday: 1,
  restart_weekdays_on_new_year: true,
  months: [
    { name: "Zarantyr (Jan)", days: 28, position: 1, associated_symbol: "Storm" },
    { name: "Olarune (Feb)", days: 28, position: 2, associated_symbol: "Sentinel" },
    { name: "Therendor (Mar)", days: 28, position: 3, associated_symbol: "Healing" },
    { name: "Eyre (Apr)", days: 28, position: 4, associated_symbol: "Making" },
    { name: "Dravago (May)", days: 28, position: 5, associated_symbol: "Handling" },
    { name: "Nymm (Jun)", days: 28, position: 6, associated_symbol: "Hospitality" },
    { name: "Lharvion (Jul)", days: 28, position: 7, associated_symbol: "Detection" },
    { name: "Barrakas (Aug)", days: 28, position: 8, associated_symbol: "Finding" },
    { name: "Rhaan (Sep)", days: 28, position: 9, associated_symbol: "Scribing" },
    { name: "Sypheros (Oct)", days: 28, position: 10, associated_symbol: "Shadow" },
    { name: "Aryth (Nov)", days: 28, position: 11, associated_symbol: "Passage" },
    { name: "Vult (Dec)", days: 28, position: 12, associated_symbol: "Warding" },
    { name: "", days: 2, position: 13, associated_symbol: "", no_weekdays: true }
  ],
  weekdays: [
    { name: "Sul (Sunday)", position: 1 },
    { name: "Mol (Monday)", position: 2 },
    { name: "Zol (Tuesday)", position: 3 },
    { name: "Wir (Wednesday)", position: 4 },
    { name: "Zor (Thursday)", position: 5 },
    { name: "Far (Friday)", position: 6 },
    { name: "Sar (Saturday)", position: 7 },
  ],
  holidays: [
    { name: "Crystalfall", type: "Celebration", year_day: 37, mmdd: 209, description: "Celebration/Contest	Sharn	To celebrate the fallen Glass Tower sculptors carve ice towers and throw them into the Dagger River" },
    { name: "The Day of Mourning", type: "Anniversary", year_day: 48, mmdd: 220, description: "Anniversary	Khorvaire	This marks The Day of Mourning, when Cyre was destroyed. People observe in many ways." },
    { name: "Sun's Blessing", type: "Holy Event", year_day: 71, mmdd: 315 },
    { name: "Aureon's Crown", type: "Holy Event", year_day: 138, mmdd: 526 },
    { name: "Brightblade", type: "Holy Event", year_day: 152, mmdd: 612 },
    { name: "The Race of Eight Winds", type: "Sporting Event", year_day: 191, mmdd: 723, observed_by: "Sharn", description: "Contestants fly all around Sharn in attempt to win the race and prestige." },
    { name: "The Hunt", type: "Holy Event", year_day: 200, mmdd: 804 },
    { name: "Fathen's Fall", type: "Holy Event", year_day: 221, mmdd: 825 },
    { name: "Boldrei's Feast", type: "Holy Event", year_day: 233, mmdd: 909 },
    { name: "Wildnight", type: "Holy Event", year_day: 270, mmdd: 1018, length: 2, observed_by: "Khorvaire", description: "At sunset on Wildnight, worshipers and the nonfaithful alike give vent to their passions, in a raucous festival dedicated to the Fury." },
    { name: "Thronehold", type: "Holy Event", year_day: 291, mmdd: 1111, observed_by: "Khorvaire", description: "This marks the day of the signing of the Treaty of Thronehold and the end of The Last War." },
    { name: "Revelation's Day", type: "Holy Event", year_day: 325, mmdd: 1217, observed_by: "Blood of Vol", description: "A day venerated by the Blood of Vol. It's on the 13th day of the 13th month, which other religions don't recognize as a month." },
    { name: "Long Shadows", type: "Holy Event", year_day: 336, mmdd: 1228, length: 3, observed_by: "Khorvaire", description: "Dark magic dominates this three-day period that honors the Shadow." },
  ],
  seasons: [
    { name: "Spring", mmdd_start: 320, mmdd_end: 620, year_day: 77, length: 85 },
    { name: "Summer", mmdd_start: 621, mmdd_end: 921, year_day: 162, length: 85 },
    { name: "Autumn", mmdd_start: 922, mmdd_end: 1220, year_day: 247, length: 83 },
    { name: "Winter", mmdd_start: 1221, mmdd_end: 319, year_day: 330, length: 85 },
  ],
  years_per_leap_year: 0 // No Leap Year
}

/** 
 * This is my Blood of Vol calendar that works with GALIFAR_CALENDAR_338, which is my revised Galifar calendar 
 * that has a 338 day year, with the last 2 days not being considered part of any month. 
 * I'm also moving the holiday Long Shadows to be during those 2 days.
 */
export const BLOOD_OF_VOL_CALENDAR_338: CalendarSystem = {
  true_id: "BLOOD_OF_VOL",
  type: "Galifar",
  name: "Blood of Vol",
  abbrev: "BV",
  very_first_wkday: 1,
  restart_weekdays_on_new_year: true,
  months: [
    { name: "Zarantyr (Jan)", days: 26, position: 1, associated_symbol: "Storm" },
    { name: "Olarune (Feb)", days: 26, position: 2, associated_symbol: "Sentinel" },
    { name: "Therendor (Mar)", days: 26, position: 3, associated_symbol: "Healing" },
    { name: "Eyre (Apr)", days: 26, position: 4, associated_symbol: "Making" },
    { name: "Dravago (May)", days: 26, position: 5, associated_symbol: "Handling" },
    { name: "Nymm (Jun)", days: 26, position: 6, associated_symbol: "Hospitality" },
    { name: "Lharvion (Jul)", days: 26, position: 7, associated_symbol: "Detection" },
    { name: "Barrakas (Aug)", days: 26, position: 8, associated_symbol: "Finding" },
    { name: "Rhaan (Sep)", days: 26, position: 9, associated_symbol: "Scribing" },
    { name: "Sypheros (Oct)", days: 26, position: 10, associated_symbol: "Shadow" },
    { name: "Aryth (Nov)", days: 26, position: 11, associated_symbol: "Passage" },
    { name: "Vult (Dec)", days: 26, position: 12, associated_symbol: "Warding" },
    { name: "Crya", days: 26, position: 13, associated_symbol: "Death" }
  ],
  weekdays: [
    { name: "Sul (Sunday)", position: 1 },
    { name: "Mol (Monday)", position: 2 },
    { name: "Zol (Tuesday)", position: 3 },
    { name: "Wir (Wednesday)", position: 4 },
    { name: "Zor (Thursday)", position: 5 },
    { name: "Far (Friday)", position: 6 },
    { name: "Sar (Saturday)", position: 7 },
  ],
  holidays: [
    { name: "Crystalfall", type: "Celebration", year_day: 37, mmdd: 211, description: "Celebration/Contest	Sharn	To celebrate the fallen Glass Tower sculptors carve ice towers and throw them into the Dagger River" },
    { name: "The Day of Mourning", type: "Anniversary", year_day: 48, mmdd: 222, description: "Anniversary	Khorvaire	This marks The Day of Mourning, when Cyre was destroyed. People observe in many ways." },
    { name: "Sun's Blessing", type: "Holy Event", year_day: 71, mmdd: 319 },
    { name: "Aureon's Crown", type: "Holy Event", year_day: 138, mmdd: 608 },
    { name: "Brightblade", type: "Holy Event", year_day: 152, mmdd: 622 },
    { name: "The Race of Eight Winds", type: "Sporting Event", year_day: 191, mmdd: 809, observed_by: "Sharn", description: "Contestants fly all around Sharn in attempt to win the race and prestige." },
    { name: "The Hunt", type: "Holy Event", year_day: 200, mmdd: 818 },
    { name: "Fathen's Fall", type: "Holy Event", year_day: 221, mmdd: 913 },
    { name: "Boldrei's Feast", type: "Holy Event", year_day: 233, mmdd: 925 },
    { name: "Wildnight", type: "Holy Event", year_day: 270, mmdd: 1110, length: 2, observed_by: "Khorvaire", description: "At sunset on Wildnight, worshipers and the nonfaithful alike give vent to their passions, in a raucous festival dedicated to the Fury." },
    { name: "Thronehold", type: "Holy Event", year_day: 291, mmdd: 1205, observed_by: "Khorvaire", description: "This marks the day of the signing of the Treaty of Thronehold and the end of The Last War." },
    { name: "Revelation's Day", type: "Holy Event", year_day: 325, mmdd: 1313, observed_by: "Blood of Vol", description: "A day venerated by the Blood of Vol. It's on the 13th day of the 13th month, which other religions don't recognize as a month." },
    { name: "Long Shadows", type: "Holy Event", year_day: 336, mmdd: 1324, length: 3, observed_by: "Khorvaire", description: "Dark magic dominates this three-day period that honors the Shadow." },
  ],
  seasons: [
    { name: "Spring", mmdd_start: 324, mmdd_end: 804, year_day: 77, length: 85 },
    { name: "Summer", mmdd_start: 805, mmdd_end: 1111, year_day: 162, length: 85 },
    { name: "Autumn", mmdd_start: 1112, mmdd_end: 1316, year_day: 247, length: 83 },
    { name: "Winter", mmdd_start: 1317, mmdd_end: 323, year_day: 330, length: 85 },
  ],
  years_per_leap_year: 0 // No Leap Year
}

export const CALENDAR_OPTIONS: DisablableOption[] = [
  {
    id: "",
    text: "None"
  },
  {
    id: "STANDARD",
    text: "Standard"
  },
  {
    id: "GALIFAR",
    text: "Galifar"
  },
  {
    id: "GALIFAR_REVISED",
    text: "Galifar Revised"
  },
  {
    id: "BLOOD_OF_VOL",
    text: "Blood of Vol"
  },
];

// value format: YYYY MM DD -> year*10000 + month*100 + day
export const parseDate = (value: number) => {
  const year = Math.floor(value / 10000);
  const remainder = value % 10000;
  const month = Math.floor(remainder / 100);
  const day = remainder % 100;
  return { year, month, day };
}
export const parseMMDD = (mmdd: number) => {
  const day = mmdd % 100;
  const month = (mmdd - day) / 100;
  return { month, day };
}

export const dateString = (date: number, calendar_id: string, format: string = "default") => {
  const calendar = CALENDARS.find(o => o.true_id === calendar_id);
  if (calendar) {
    const parsed_date = parseDate(date);
    const calendar_month = calendar.months.find(o => o.position === parsed_date.month);
    if (calendar_month) {
      // if (format === "default") { // I might make use of different formats later.
      return `${calendar_month.name.length > 0 ? calendar_month.name : "Nameless"} ${parsed_date.day}, ${parsed_date.year} ${calendar.abbrev}`;
      // }
    }
  }
  return "Unknown";
}

export const mmddString = ({ mmdd, calendar, calendar_id = "", format = "default" }: 
  { mmdd: number, calendar?: CalendarSystem, calendar_id?: string, format?: string }) => {
  const use_calendar = calendar ? calendar : CALENDARS.find(o => o.true_id === calendar_id);
  if (use_calendar) {
    const parsed_date = parseMMDD(mmdd);
    const calendar_month = use_calendar.months.find(o => o.position === parsed_date.month);
    if (calendar_month) {
      // if (format === "default") { // I might make use of different formats later.
      return `${calendar_month.name.length > 0 ? calendar_month.name : "Nameless"} ${parsed_date.day}`;
      // }
    }
  }
  return "Unknown";
}

export const mmddRangeString = ({ mmdd_start, mmdd_end, length, calendar, calendar_id = "", format = "default" }: 
  { mmdd_start: number, mmdd_end?: number, length?: number, calendar?: CalendarSystem, calendar_id?: string, format?: string }) => {
  const use_calendar = calendar ? calendar : CALENDARS.find(o => o.true_id === calendar_id);
  if (use_calendar) {
    const use_mmdd_end = mmdd_end ? mmdd_end : length ? addDaysToMMDD({ calendar: use_calendar, mmdd: mmdd_start, offset: length }) : undefined;
    if (use_mmdd_end !== undefined && mmdd_start !== use_mmdd_end) {
      return `${mmddString({ mmdd: mmdd_start, calendar })} to ${mmddString({ mmdd: use_mmdd_end, calendar })}`;
    } else {
      return mmddString({ mmdd: mmdd_start, calendar });
    }
  }
  return "Unknown";
}

export const composeDate = (year: number, month: number, day: number): number => {
  return year * 10000 + month * 100 + day;
}

export const isLeapYear = (calendar: CalendarSystem, year: number): boolean => {
  return calendar.years_per_leap_year > 0 && year % calendar.years_per_leap_year === 0;
}

export const getDaysInMonth = (calendar: CalendarSystem, month: CalendarMonth, year: number): number => {
  let days = month.days;
  if (month.leap_day && isLeapYear(calendar, year)) days += 1;
  return days;
}

// 0-indexed weekday, or null if this day falls in a no_weekdays month
export const getWeekdayIndex = (calendar: CalendarSystem, year: number, month: number, day: number): number | null => {
  const monthObj = calendar.months.find(m => m.position === month);
  if (!monthObj || monthObj.no_weekdays) return null;

  const numWeekdays = calendar.weekdays.length;
  const sortedMonths = [...calendar.months].sort((a, b) => a.position - b.position);

  const daysBeforeMonthInYear = (y: number) => {
    let total = 0;
    for (const m of sortedMonths) {
      if (m.position >= month) break;
      if (!m.no_weekdays) total += getDaysInMonth(calendar, m, y);
    }
    return total;
  };

  if (calendar.restart_weekdays_on_new_year) {
    const count = daysBeforeMonthInYear(year) + day; // 1-indexed position within the year's cycle
    return (count - 1) % numWeekdays;
  }

  // Epoch-based: accumulate weekday-counted days from year 1 up through this date.
  // (Cached per-year via useMemo below so this isn't recomputed on every keystroke.)
  let totalDays = 0;
  for (let y = 1; y < year; y++) {
    for (const m of sortedMonths) {
      if (!m.no_weekdays) totalDays += getDaysInMonth(calendar, m, y);
    }
  }
  totalDays += daysBeforeMonthInYear(year) + day;
  return (totalDays - 1) % numWeekdays;
}

export const getNthWeekdayOfMonth = (
  calendar: CalendarSystem,
  year: number,
  month: number,
  n: number,
  wkdayPosition: number
): number | null => {
  const monthObj = calendar.months.find(m => m.position === month);
  if (!monthObj || monthObj.no_weekdays) return null;

  const daysInMonth = getDaysInMonth(calendar, monthObj, year);
  let count = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const wi = getWeekdayIndex(calendar, year, month, d);
    if (wi !== null && wi + 1 === wkdayPosition) {
      count++;
      if (count === n) return d;
    }
  }
  return null;
}

// Advance an mmdd by `offset` days, respecting month lengths (for multi-day holidays).
export const addDaysToMmdd = (calendar: CalendarSystem, year: number, mmdd: number, offset: number): number => {
  let month = Math.floor(mmdd / 100);
  let day = (mmdd % 100) + offset;
  const sortedMonths = [...calendar.months].sort((a, b) => a.position - b.position);

  while (true) {
    const monthObj = sortedMonths.find(m => m.position === month);
    if (!monthObj) break;
    const daysInMonth = getDaysInMonth(calendar, monthObj, year);
    if (day <= daysInMonth) break;
    day -= daysInMonth;
    const idx = sortedMonths.findIndex(m => m.position === month);
    if (idx === sortedMonths.length - 1) break; // don't roll into next year here
    month = sortedMonths[idx + 1].position;
  }
  return month * 100 + day;
}

export const addDaysToMMDD = ({ calendar, mmdd, offset }: 
  { calendar: CalendarSystem, mmdd: number, offset: number }) => {
  const year_day = getYearDayFromMMDD({ calendar, mmdd });
  return getMMDDFromYearDay({ calendar, year_day: year_day + offset, allow_reset: true });
}

export const findHolidaysForDate = (calendar: CalendarSystem, year: number, month: number, day: number) => {
  const mmdd = month * 100 + day;
  const holidays: CalendarHoliday[] = [];

  for (const holiday of calendar.holidays) {
    const length = holiday.length ?? 1;

    let startMmdd: number | undefined = holiday.mmdd;

    if (startMmdd === undefined && holiday.nth_wkday) {
      const targetMonth = holiday.nth_wkday.mm ?? month;
      if (targetMonth !== month) continue; // "every month" case: only relevant if we're in that month
      const d = getNthWeekdayOfMonth(calendar, year, targetMonth, holiday.nth_wkday.n, holiday.nth_wkday.wkday);
      if (d === null) continue;
      startMmdd = targetMonth * 100 + d;
    }

    if (startMmdd === undefined) continue;

    if (length === 1) {
      if (startMmdd === mmdd) {
        holidays.push(holiday);
      }
    } else {
      for (let i = 0; i < length; i++) {
        if (addDaysToMmdd(calendar, year, startMmdd, i) === mmdd) {
          holidays.push(holiday);
        }
      }
    }
  }
  return holidays;
}

export const findSeasonForDate = (calendar: CalendarSystem, month: number, day: number) => {
  const mmdd = month * 100 + day;
  return calendar.seasons.find(s => {
    if (s.mmdd_start <= s.mmdd_end) {
      return mmdd >= s.mmdd_start && mmdd <= s.mmdd_end;
    }
    // wraps across the new year (e.g. Winter: Dec 21 -> Mar 19)
    return mmdd >= s.mmdd_start || mmdd <= s.mmdd_end;
  }) ?? null;
}

export const findHolidaysForMonth = (calendar: CalendarSystem, month: number) => {
  const calendar_month = calendar.months.find(o => o.position === month);
  const month_range = calendar_month ? getMonthRange(calendar_month) : { start: 0, end: 0 };
  return calendar.holidays.filter(h => common_util.doNumRangesOverlap({ range1: month_range, range2: getHolidayRange(calendar, h), resets: true, true_on_kiss: true }));
}

export const getMonthRange = (calendar_month: CalendarMonth) => {
  return { start: calendar_month.position * 100, end: (calendar_month.position * 100 + calendar_month.days) };
}

export const getSeasonRange = (calendar_season: CalendarSeason) => {
  return { start: calendar_season.mmdd_start, end: calendar_season.mmdd_end };
}

export const getMMDDFromYearDay = ({ calendar, year_day, allow_reset = false }: 
  { 
    calendar: CalendarSystem, 
    year_day: number, 
    allow_reset?: boolean // This is for situations where it might not be a real year_day, but is a number of days added to a year_day, which can take it past the end of the year and into the next, in which case we still want it to work.
  }) => {
  let days_left = year_day;
  let current_month = calendar.months[0];
  while (current_month && days_left > current_month.days) {
    days_left -= current_month.days;
    current_month = calendar.months[current_month.position];
    if (!current_month && allow_reset) {
      current_month = calendar.months[0];
    }
  }
  if (current_month) {
    return current_month.position * 100 + days_left;
  }
  console.error("Invalid Year Day");
  console.error(calendar);
  console.error(year_day);
  debugger;
  return -1; 
}

export const getYearDayFromMMDD = ({ calendar, mmdd }: 
  { 
    calendar: CalendarSystem, 
    mmdd: number, 
  }) => {
  const { day, month } = parseMMDD(mmdd);
  let year_day = day;
  for (let i = 1; i < month; ++i) {
    const calendar_month = calendar.months.find(o => o.position === i);
    if (calendar_month) {
      year_day += calendar_month.days;
    }
  }
  return year_day;
}

export const getHolidayRange = (calendar: CalendarSystem, holiday: CalendarHoliday) => {
  // if (holiday.name.includes("Long Shadows")) {
  //   debugger;
  // }
  const mmdd = holiday.mmdd ? holiday.mmdd : holiday.year_day ? getMMDDFromYearDay({ calendar, year_day: holiday.year_day }) : -1;
  if (holiday.length === undefined || holiday.length < 2) {
    return { start: mmdd, end: mmdd };
  } else {
    const year_day = holiday.year_day ? holiday.year_day : getYearDayFromMMDD({ calendar, mmdd });
    const end = getMMDDFromYearDay({ calendar, year_day: year_day + holiday.length, allow_reset: true });
    return { start: mmdd, end };
  }
}

export const findSeasonsForMonth = (calendar: CalendarSystem, month: number) => {
  const calendar_month = calendar.months.find(o => o.position === month);
  const month_range = calendar_month ? getMonthRange(calendar_month) : { start: 0, end: 0 };
  return calendar.seasons.filter(s => common_util.doNumRangesOverlap({ range1: month_range, range2: getSeasonRange(s), resets: true, true_on_kiss: true }));
}

export const getWeekday = (calendar_id: string, date: number) => {
  const calendar = CALENDARS.find(o => o.true_id === calendar_id);
  if (calendar) {
    const parsed_date = parseDate(date);
    const wkday_index = getWeekdayIndex(calendar, parsed_date.year, parsed_date.month, parsed_date.day);
    if (wkday_index !== null) {
      return calendar.weekdays[wkday_index];
    }
  }
  return null;
}

export const CALENDARS = [STANDARD_CALENDAR, GALIFAR_CALENDAR, GALIFAR_CALENDAR_338, BLOOD_OF_VOL_CALENDAR_338];